import React from "react";
import { Box, Typography, Chip, IconButton, Tooltip } from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import HPButton from "@/components/common/Button";
import HPDropdown from "@/components/common/Dropdown";
import type { ScorecardBrand, ScorecardBrandMapping } from "@/models/Scorecard";
import { getEffectiveMappingStatus } from "@/utils/scorecard-utils";

const STATUS_CHIP: Record<
  ScorecardBrandMapping["status"],
  { label: string; color: "success" | "warning" | "info" | "default" }
> = {
  matched: { label: "Matched", color: "success" },
  manual: { label: "Manually mapped", color: "info" },
  unresolved: { label: "Unresolved", color: "warning" },
  unavailable: { label: "Unavailable", color: "default" },
};

interface MappingRowProps {
  brand: ScorecardBrand;
  mapping: ScorecardBrandMapping | undefined;
  availableOptions: { value: string; label: string }[];
  onManualBind: (brandId: string, responseId: string) => void;
  onMarkUnavailableClick: (brand: ScorecardBrand) => void;
  onRestoreMapping: (brandId: string) => void;
  /** This brand's resolved status on the row's Scorecard Row Base (an
   *  Existing Question Row's own mapping for the same brand), when this row
   *  has one — folded into the displayed chip via getEffectiveMappingStatus
   *  so this panel never shows "Matched" while the Base it depends on is
   *  actually unresolved/unavailable/manually mapped for that brand. Resolution
   *  actions below still act on this row's own mapping only. */
  baseStatus?: ScorecardBrandMapping["status"];
}

/** One brand's mapping status + resolution controls — shared by a row's own
 *  mapping panel (Question or Metric row) and a New Question Base's mapping
 *  panel. Extracted from BrandMappingStep to keep it under the file-size limit. */
const MappingRow: React.FC<MappingRowProps> = ({
  brand,
  mapping,
  availableOptions,
  onManualBind,
  onMarkUnavailableClick,
  onRestoreMapping,
  baseStatus,
}) => {
  const status = mapping?.status ?? "unresolved";
  const chip = STATUS_CHIP[getEffectiveMappingStatus(status, baseStatus)];

  return (
    <Box
      sx={{
        px: 1.5,
        py: 0.75,
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap>
            {brand.label}
          </Typography>
          <Typography
            variant="caption"
            color={brand.role === "primary" ? "primary.main" : "text.secondary"}
          >
            {brand.role === "primary" ? "Primary" : "Competitor"}
          </Typography>
        </Box>
        <Chip
          label={chip.label}
          color={chip.color}
          size="small"
          sx={{ height: 20, fontSize: "0.7rem", flexShrink: 0 }}
        />
      </Box>

      {status === "unavailable" ? (
        <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          {mapping?.unavailableReason && (
            <Tooltip title={mapping.unavailableReason}>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Reason:
                </Box>{" "}
                {mapping.unavailableReason}
              </Typography>
            </Tooltip>
          )}
          <HPButton variant="text" size="small" onClick={() => onRestoreMapping(brand.id)}>
            Restore mapping
          </HPButton>
        </Box>
      ) : (
        <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <HPDropdown
              label=""
              size="small"
              className="[&>label]:hidden"
              value={mapping?.boundResponseId ?? ""}
              onChange={(value) => onManualBind(brand.id, value)}
              placeholder="Select response category"
              options={availableOptions}
            />
          </Box>
          <Tooltip title="Mark Unavailable">
            <IconButton size="small" sx={{ flexShrink: 0 }} onClick={() => onMarkUnavailableClick(brand)}>
              <BlockIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default MappingRow;
